import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { apiRoutes } from '../api';
import { getConfig } from '../config/environment';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('User');
      if (saved) {
        const p = JSON.parse(saved);
        return { id: p.id, email: p.email, user_metadata: { first_name: p.first_name, last_name: p.last_name } };
      }
    } catch {}
    return null;
  });
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isGuest, setIsGuest] = useState(() => localStorage.getItem('isGuest') === 'true');
  const [userProfile, setUserProfile] = useState(() => {
    try {
      const saved = localStorage.getItem('User');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const refreshTimerRef = useRef(null);
  const isRefreshingRef = useRef(false);

  const { tlfId, recaptchaKey } = getConfig();

  // Map API errors to user-friendly messages.
  // Each returned object includes a `key` (translation key under auth.errors.*)
  // so components inside TranslationProvider can call t(error.key, error.message)
  // to get a localized string while falling back to the English message.
  const getFriendlyAuthError = (err, fallback) => {
    const status = err.response?.status;
    const apiMsg = err.response?.data?.payload?.message || err.response?.data?.message;
    const errors = err.response?.data?.errors || err.response?.data?.payload?.errors;
    const firstError = Array.isArray(errors) ? errors[0] : null;

    // Check for specific API error strings
    if (firstError === 'Invalid Captcha' || apiMsg?.includes?.('Captcha')) {
      return { message: 'Captcha verification failed. Please try again.', key: 'auth.errors.captchaFailed', code: 'captcha' };
    }
    if (firstError === 'Excluded' || apiMsg?.includes?.('Excluded')) {
      return { message: 'Your account has been excluded.', key: 'auth.errors.accountExcluded', code: 'excluded' };
    }
    if (apiMsg?.toLowerCase?.().includes?.('locked') || apiMsg?.toLowerCase?.().includes?.('disabled')) {
      return { message: 'Your account has been locked. Please contact support.', key: 'auth.errors.accountLocked', code: 'locked' };
    }
    if (apiMsg?.toLowerCase?.().includes?.('deactivated')) {
      return { message: 'Your account has been deactivated. Please contact support.', key: 'auth.errors.accountDeactivated', code: 'deactivated' };
    }
    if (apiMsg?.toLowerCase?.().includes?.('already exists') || apiMsg?.toLowerCase?.().includes?.('member exists')) {
      return { message: 'An account with this email already exists. Please sign in instead.', key: 'auth.errors.memberExists', code: 'exists' };
    }
    // If API returned a meaningful message, use it (no key — raw API text, not translatable)
    if (apiMsg && !apiMsg.includes('status code')) {
      return { message: apiMsg };
    }

    // Map HTTP status codes to friendly messages
    switch (status) {
      case 400:
        return fallback === 'Registration failed'
          ? { message: 'Registration failed. Please check your information and try again.', key: 'auth.errors.registrationFailed' }
          : { message: 'Invalid email or password. Please try again.', key: 'auth.errors.invalidCredentials' };
      case 401:
        return { message: 'Your session has expired. Please sign in again.', key: 'auth.errors.sessionExpired' };
      case 403:
        return { message: 'Your account has been locked. Please contact support.', key: 'auth.errors.accountLocked', code: 'locked' };
      case 429:
        return { message: 'Too many attempts. Please wait a moment and try again.', key: 'auth.errors.tooManyAttempts' };
      default:
        return { message: apiMsg || fallback };
    }
  };

  const clearAuth = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('User');
    localStorage.removeItem('isGuest');
    setUser(null);
    setSession(null);
    setUserProfile(null);
    setIsAdmin(false);
    setIsGuest(false);
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    isRefreshingRef.current = false;
  }, []);

  const loadMemberInfo = useCallback(async () => {
    try {
      const response = await api.get(apiRoutes.MemberInformation, {
        params: { organizationId: tlfId }
      });
      const payload = response.data?.payload || response.data;
      const member = payload?.member || payload;
      if (member) {
        const profile = {
          id: member.memberId,
          email: member.email,
          first_name: member.firstName || member.contactFirstName,
          last_name: member.lastName || member.contactLastName,
          phone: member.contactPhone,
          contactAddress: member.contactAddress,
          contactCity: member.contactCity,
          contactProvinceState: member.contactProvinceState,
          contactPostalZip: member.contactPostalZip,
          contactCountry: member.contactCountry,
          authenticationMethod: member.authenticationMethod,
          is_admin: false,
        };
        setUser({ id: member.memberId, email: member.email, user_metadata: { first_name: member.firstName, last_name: member.lastName } });
        setUserProfile(profile);
        setSession({ user: { id: member.memberId, email: member.email } });
        localStorage.setItem('User', JSON.stringify(profile));
        return profile;
      }
    } catch (err) {
      console.error('Failed to load member info:', err);
      clearAuth();
    }
    return null;
  }, [tlfId, clearAuth]);

  // Parse expiresAt: API returns Unix seconds (e.g. 1774548703), not ms or ISO string
  const parseExpiresAt = useCallback((raw) => {
    if (!raw) return NaN;
    const num = Number(raw);
    if (!isNaN(num)) {
      // If it looks like seconds (< year 2100 in seconds), convert to ms
      return num < 4102444800 ? num * 1000 : num;
    }
    return new Date(raw).getTime();
  }, []);

  const scheduleTokenRefresh = useCallback(() => {
    const expiresAt = localStorage.getItem('expires_at');
    if (!expiresAt) return;

    const expiresMs = parseExpiresAt(expiresAt);
    const now = Date.now();
    const refreshIn = expiresMs - now - 60000; // Refresh 1 minute before expiry

    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (isNaN(expiresMs) || refreshIn <= 0) {
      clearAuth();
      return;
    }

    refreshTimerRef.current = setTimeout(() => {
      refreshToken();
    }, Math.min(refreshIn, 2147483647)); // Cap at max setTimeout value
  }, [clearAuth, parseExpiresAt]);

  const refreshToken = useCallback(async () => {
    if (isRefreshingRef.current) return; // Prevent concurrent refreshes
    const currentRefreshToken = localStorage.getItem('refresh_token');
    if (!currentRefreshToken) {
      clearAuth();
      return;
    }

    try {
      isRefreshingRef.current = true;
      // Swap: use refresh token as access token for the refresh call
      const response = await api.post(apiRoutes.RefreshToken, {}, {
        params: { organizationId: tlfId },
        headers: { Authorization: `Bearer ${currentRefreshToken}` }
      });
      const data = response.data?.payload || response.data;
      const at = data?.accessToken || data?.AccessToken;
      if (at) {
        localStorage.setItem('access_token', at);
        localStorage.setItem('refresh_token', data.refreshToken || data.RefreshToken);
        localStorage.setItem('expires_at', data.expiresAt || data.ExpiresAt);
        isRefreshingRef.current = false;
        scheduleTokenRefresh();
      } else {
        clearAuth();
      }
    } catch (err) {
      console.error('Token refresh failed:', err);
      clearAuth();
    } finally {
      isRefreshingRef.current = false;
    }
  }, [tlfId, clearAuth, scheduleTokenRefresh]);

  const storeTokens = useCallback((data) => {
    localStorage.setItem('access_token', data.accessToken || data.AccessToken);
    localStorage.setItem('refresh_token', data.refreshToken || data.RefreshToken);
    localStorage.setItem('expires_at', data.expiresAt || data.ExpiresAt);
    scheduleTokenRefresh();
  }, [scheduleTokenRefresh]);

  // Initialize: check existing tokens or OAuth callback params
  useEffect(() => {
    let cancelled = false; // Guard against StrictMode double-mount

    const init = async () => {

      setLoading(true);
      try {
        // Check for OAuth callback params in URL (supports both secure URL fragment and legacy query string)
        const searchParams = new URLSearchParams(window.location.search);
        const hashStr = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
        const hashParams = new URLSearchParams(hashStr);

        const urlToken = hashParams.get('Access-Token') || hashParams.get('access_token') || hashParams.get('token') || searchParams.get('token') || searchParams.get('Access-Token');
        const urlRefresh = hashParams.get('Refresh-Token') || hashParams.get('refresh_token') || hashParams.get('refreshToken') || searchParams.get('refreshToken') || searchParams.get('Refresh-Token');
        const urlExpires = hashParams.get('Expires-At') || hashParams.get('expires_at') || hashParams.get('expiresAt') || searchParams.get('expiresAt') || searchParams.get('Token-Expires');

        if (urlToken && urlRefresh) {
          storeTokens({ accessToken: urlToken, refreshToken: urlRefresh, expiresAt: urlExpires });
          if (!cancelled) await loadMemberInfo();
          // Clean URL params and hash
          window.history.replaceState(window.history.state, document.title, window.location.pathname);
          if (!cancelled) setLoading(false);
          return;
        }

        // Check for existing tokens
        const token = localStorage.getItem('access_token');
        const expiresAt = localStorage.getItem('expires_at');

        if (token && expiresAt) {
          const expiresMs = parseExpiresAt(expiresAt);
          if (expiresMs > Date.now()) {
            if (!cancelled) {
              await loadMemberInfo();
              scheduleTokenRefresh();
            }
          } else {
            // Token expired — clear immediately, user must re-login
            clearAuth();
          }
        }
      } catch (err) {
        console.error('Auth init error:', err);
        if (!cancelled) clearAuth();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();

    // Listen for session expiry dispatched by the API interceptor
    const handleSessionExpired = () => clearAuth();
    window.addEventListener('auth:session-expired', handleSessionExpired);

    return () => {
      cancelled = true;
      window.removeEventListener('auth:session-expired', handleSessionExpired);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, []);

  const signIn = async (email, password, captchaToken) => {
    try {
      const response = await api.post(`${apiRoutes.EmailLogin}?organizationId=${tlfId}`, {
        Email: email,
        Password: password,
        reCaptcha: captchaToken || '',
      });
      const data = response.data?.payload || response.data;
      if (data?.accessToken || data?.AccessToken) {
        storeTokens(data);
        const profile = await loadMemberInfo();
        return { data: { user: profile }, error: null };
      }
      return { data: null, error: { message: data?.message || 'Login failed' } };
    } catch (err) {
      const friendly = getFriendlyAuthError(err, 'Login failed');
      return { data: null, error: friendly };
    }
  };

  const signUp = async ({ firstName, lastName, email, password, confirmPassword, captchaToken }) => {
    try {
      const response = await api.post(`${apiRoutes.EmailRegister}?organizationId=${tlfId}`, {
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        Password: password,
        ConfirmPassword: confirmPassword,
        reCaptcha: captchaToken || '',
      });
      const data = response.data?.payload || response.data;
      if (data?.accessToken || data?.AccessToken) {
        storeTokens(data);
        const profile = await loadMemberInfo();
        return { data: { user: profile }, error: null };
      }
      return { data: null, error: { message: data?.message || 'Registration failed' } };
    } catch (err) {
      const friendly = getFriendlyAuthError(err, 'Registration failed');
      return { data: null, error: friendly };
    }
  };

  const signOut = async () => {
    clearAuth();
    return { error: null };
  };

  const loginAsGuest = async ({ firstName, lastName, email, phone, preferredLanguage }) => {
    try {
      const response = await api.post(apiRoutes.GuestCheckoutCreate, {
        OrganizationId: tlfId,
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        Phone: phone,
        PreferredLanguage: preferredLanguage || localStorage.getItem('i18nextLng') || 'en',
      });
      const data = response.data;
      if (data?.accessToken) {
        storeTokens(data);
        localStorage.setItem('isGuest', 'true');
        setIsGuest(true);
        const profile = await loadMemberInfo();
        return { data: { user: profile, memberId: data.memberId }, error: null };
      }
      return { data: null, error: { message: data?.error || 'Guest checkout setup failed' } };
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Guest checkout setup failed';
      const errorKey = err.response?.data?.errorKey;
      return { data: null, error: { message: msg, errorKey } };
    }
  };

  const resetPassword = async (email, captchaToken) => {
    try {
      // The forgot-password endpoint binds email/recaptcha from the query string
      // (they are simple action parameters on the controller, not a [FromBody] model).
      const params = new URLSearchParams({
        organizationId: tlfId,
        email: email ?? '',
        recaptcha: captchaToken ?? '',
      });
      const response = await api.post(`${apiRoutes.ForgotPassword}?${params.toString()}`, null);
      return { data: response.data, error: null };
    } catch (err) {
      const msg = err.response?.data?.payload?.message || err.response?.data?.message || err.message || 'Password reset failed';
      return { data: null, error: { message: msg } };
    }
  };

  const confirmPasswordReset = async (memberId, token, password, confirmPassword, recaptchaToken) => {
    try {
      const response = await api.put(`${apiRoutes.PasswordReset}?memberId=${memberId}&token=${encodeURIComponent(token)}`, {
        NewPassword: password,
        ConfirmPassword: confirmPassword,
        Token: token,
        ReCaptcha: recaptchaToken ?? '',
      });
      return { data: response.data, error: null };
    } catch (err) {
      const msg = err.response?.data?.payload?.message || err.response?.data?.message || err.message || 'Password reset failed';
      return { data: null, error: { message: msg } };
    }
  };

  const changePassword = async (currentPassword, newPassword, confirmPassword) => {
    try {
      const response = await api.put(apiRoutes.ChangePassword, {
        CurrentPassword: currentPassword,
        NewPassword: newPassword,
        ConfirmPassword: confirmPassword,
      });
      return { data: response.data, error: null };
    } catch (err) {
      const msg = err.response?.data?.payload?.message || err.response?.data?.message || err.message || 'Password change failed';
      return { data: null, error: { message: msg } };
    }
  };

  const getSocialLoginUrl = (provider, siteDetails) => {
    switch (provider) {
      case 'facebook': return siteDetails?.facebookLoginUrl;
      case 'google': return siteDetails?.googlePlusLoginUrl;
      case 'apple': return siteDetails?.appleLoginUrl;
      case 'microsoft': return siteDetails?.microsoftLoginUrl;
      default: return null;
    }
  };

  const isLoggedIn = !!user && !!localStorage.getItem('access_token');

  const value = {
    user,
    session,
    loading,
    isAdmin,
    isGuest,
    userProfile,
    isLoggedIn,
    signIn,
    signUp,
    signOut,
    loginAsGuest,
    resetPassword,
    confirmPasswordReset,
    changePassword,
    getSocialLoginUrl,
    loadMemberInfo,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
